import { CalendarCheck, ClipboardList, CloudSun } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { PageHero, CtaButton, CtaBand, SectionHead, usePageMeta } from "@/components/shared";

const REPORTS = [
  [CalendarCheck, "Scheduled Inspections", "Weekly, bi-weekly or monthly walk-throughs for owners who travel or live overseas. Exterior, interior systems, security and developing issues — documented and reported."],
  [ClipboardList, "Pre-Arrival & Post-Stay Inspections", "Every guest arrival walked into a verified, guest-ready home. Every departure followed by a documented condition check before the next turnover begins."],
  [CloudSun, "Storm & Weather Checks", "Before and after significant weather, we check your property and report immediately — the single most valuable service an overseas owner can have."],
];

export default function Inspections() {
  usePageMeta("Property Inspections", "Scheduled Bermuda property inspections with photo reports for homeowners who travel or live overseas.");
  return (
    <main>
      <PageHero
        overline="Property Inspections"
        lines={["Your Eyes", "On the Island."]}
        copy="Scheduled inspections with dated photographic reports — so you always know your Bermuda home is exactly as it should be, from anywhere in the world."
        image={IMAGES.livingView}
      >
        <CtaButton to="/assessment" variant="accent" testid="inspections-assessment-btn">Get a Property Assessment</CtaButton>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <SectionHead
          overline="Inspection Options"
          title="Documented. Photographed. Reported."
          copy="Every inspection produces a clear written report with photographs, covering condition, security, weather exposure and anything requiring your attention or decision."
        />
        <div className="mt-14 grid gap-10 lg:grid-cols-3">
          {REPORTS.map(([Icon, title, copy], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="trace-card h-full border border-navy/10 bg-white p-9">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-pink-soft text-bermuda-dark">
                  <Icon size={18} strokeWidth={1.5} />
                </span>
                <h3 className="mt-6 font-serif text-2xl leading-tight text-navy">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-navy/60">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-14 max-w-3xl font-serif text-2xl leading-relaxed text-navy/80">
            "Nothing about your property should surprise you. You should learn about issues from our report —{" "}
            <span className="italic text-bermuda-dark">never from a guest's review.</span>"
          </p>
        </Reveal>
      </section>

      <CtaBand title="Travel with peace of mind." copy="Tell us how often you'd like your property inspected and we'll build it into a simple plan." />
    </main>
  );
}
